import { Injectable, inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, combineLatest, map, startWith } from 'rxjs';
import { AppTileConfig } from '../models/application.model';
import { RuntimeConfigService } from './runtime-config.service';

export interface ApplicationAccessState {
  loading: boolean;
  isAuthenticated: boolean;
  name: string | undefined;
  applications: AppTileConfig[];
  shouldRedirect: boolean;
  redirectTarget: AppTileConfig | null;
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationAccessService {
  private auth = inject(AuthService);
  private configService = inject(RuntimeConfigService)
  readonly state$: Observable<ApplicationAccessState> = combineLatest([
    this.auth.isAuthenticated$,
    this.auth.isLoading$,
    this.auth.idTokenClaims$.pipe(startWith(null))
  ]).pipe(
    map(([isAuthenticated, isLoading, claims]) => {
      if (isLoading) {
        return {
          loading: true,
          isAuthenticated: false,
          name: "",
          applications: [],
          shouldRedirect: false,
          redirectTarget: null
        };
      }

      if (!isAuthenticated || !claims) {
        return {
          loading: false,
          isAuthenticated: false,
          name: "",
          applications: [],
          shouldRedirect: false,
          redirectTarget: null
        };
      }

      const applicationAccess = this.parseApplicationAccess(claims);
      const applications = this.filterApplications(applicationAccess);
      const enableAutoRedirect = this.isAutoRedirectEnabled(claims);
      const redirectTarget = enableAutoRedirect && applications.length === 1 ? this.getHighestPriorityApp(applications) : null;

      return {
        loading: false,
        isAuthenticated: true,
        name: claims.name,
        applications,
        shouldRedirect: enableAutoRedirect && redirectTarget !== null,
        redirectTarget
      };
    })
  );

  private parseApplicationAccess(claims: Record<string, unknown>): string[] {
    const accessClaim = claims['assignedApplications'];
    if (!accessClaim || !Array.isArray(accessClaim)) {
      return [];
    }

    return accessClaim
      .filter((app): app is string => typeof app === 'string' && app.length > 0)
      .map(app => app.trim().toLowerCase());
  }

  private filterApplications(accessList: string[]): AppTileConfig[] {
    const configuredApps = this.configService.getConfig()?.applications;

    if (!configuredApps || !Array.isArray(configuredApps)) {
      return [];
    }

    return configuredApps.filter(app =>
      accessList.includes(app.id.toLowerCase())
    );
  }

  private getHighestPriorityApp(apps: AppTileConfig[]): AppTileConfig | null {
    if (apps.length === 0) return null;
    return apps.reduce((highest, app) =>
      app.redirectPriority > highest.redirectPriority ? app : highest
    );
  }

  private isAutoRedirectEnabled(claims: Record<string, unknown>): boolean {
    const config = this.configService.getConfig();
    const email = typeof claims['email'] === 'string' ? claims['email'] : '';
    const isGcmUser = email.toLowerCase().endsWith('@gcmlp.com');

    if (isGcmUser) {
      return config?.enableAutoRedirectGcmUsers === true;
    }
    return config?.enableAutoRedirect === true;
  }
}
